import React from 'react';
import { motion } from 'framer-motion';
import type { ViewType } from '../data/homepageData';
import { BENTO_BAR_HEIGHTS, LEADERBOARD_DATA } from '../data/homepageData';

interface PlatformBentoProps {
  view: ViewType;
}

/** Rank colour map for the leaderboard */
const RANK_COLORS: Record<1 | 2 | 3, string> = {
  1: '#fbbf24',
  2: '#d1d5db',
  3: '#f97316',
};

/**
 * Bento-grid platform section with:
 * - Analytics bar chart (col-span 8)
 * - Creator leaderboard (col-span 4)
 * - Social integrations (col-span 5)
 * - Payouts / budget card (col-span 7)
 * All content switches based on the current view.
 */
const PlatformBento: React.FC<PlatformBentoProps> = ({ view }) => {
  const payoutValue = view === 'creator' ? '$1,284.50' : '$24,680';
  const payoutLabel = view === 'creator' ? 'Next Payout' : 'Total Spend';
  const payoutStatus = view === 'creator' ? 'Processing' : 'On Track';

  return (
    <section
      id="platform"
      className="relative"
      style={{ padding: 'clamp(64px, 10vw, 140px) 0' }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <div className="text-center">
          <div
            className="inline-flex items-center gap-2 mb-4"
            style={{
              fontSize: '12px',
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: '0.12em',
              color: 'var(--cv-accent)',
            }}
          >
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
            PLATFORM
            <span style={{ width: '24px', height: '1px', background: 'var(--cv-accent)', display: 'block' }} />
          </div>

          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="font-extrabold tracking-tight mb-4"
            style={{
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-2xl)',
              letterSpacing: '-0.02em',
            }}
          >
            Built for <span className="cv-text-gradient">performance</span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            style={{
              fontSize: 'var(--text-base)',
              color: 'var(--cv-text-muted)',
              maxWidth: '560px',
              lineHeight: 1.7,
              margin: '0 auto',
            }}
          >
            {view === 'creator'
              ? 'Track every view, every dollar, every milestone — all in real time.'
              : 'Monitor every campaign, every creator, every metric — from one dashboard.'}
          </motion.p>
        </div>

        {/* Bento grid — 12-column */}
        <div
          className="mt-14"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, 1fr)',
            gap: '16px',
          }}
        >
          {/* ---- Analytics card (8/12) ---- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="cv-bento-span-8 p-8 overflow-hidden"
            style={{
              gridColumn: 'span 8',
              background: 'var(--cv-surface)',
              border: '1px solid var(--cv-border)',
              borderRadius: 'var(--radius-lg)',
              transition: 'border-color 180ms, box-shadow 180ms',
            }}
            whileHover={{
              borderColor: 'var(--cv-border-accent)',
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.06)',
            }}
          >
            <div
              className="mb-3"
              style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--cv-accent)',
              }}
            >
              {view === 'creator' ? 'EARNINGS' : 'CAMPAIGN PERFORMANCE'}
            </div>
            <div
              className="font-bold mb-2"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)' }}
            >
              {view === 'creator' ? 'Watch your earnings grow' : 'Track every metric in real-time'}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--cv-text-muted)', lineHeight: 1.6 }}>
              {view === 'creator'
                ? 'Real-time earnings dashboard with breakdowns by campaign, platform, and time period.'
                : 'Cross-creator analytics showing views, engagement, spend, and ROAS across all campaigns.'}
            </div>

            {/* Mini bar chart */}
            <div
              className="mt-6 flex items-end gap-1.5"
              style={{ height: '120px', padding: '0 4px' }}
            >
              {BENTO_BAR_HEIGHTS.map((h, i) => (
                <div key={i} className="cv-bento-bar" style={{ height: `${h}%` }} />
              ))}
            </div>
          </motion.div>

          {/* ---- Leaderboard card (4/12) ---- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="cv-bento-span-4 p-8"
            style={{
              gridColumn: 'span 4',
              background: 'var(--cv-surface)',
              border: '1px solid var(--cv-border)',
              borderRadius: 'var(--radius-lg)',
              transition: 'border-color 180ms, box-shadow 180ms',
            }}
            whileHover={{
              borderColor: 'var(--cv-border-accent)',
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.06)',
            }}
          >
            <div
              className="mb-3"
              style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--cv-accent)',
              }}
            >
              LEADERBOARD
            </div>
            <div
              className="font-bold mb-5"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)' }}
            >
              Top creators
            </div>

            <div className="flex flex-col gap-2.5">
              {LEADERBOARD_DATA.map((item) => (
                <div
                  key={item.rank}
                  className="flex items-center gap-3"
                  style={{
                    padding: '10px 14px',
                    background: 'var(--cv-surface-2)',
                    borderRadius: 'var(--radius-md)',
                    fontSize: '14px',
                  }}
                >
                  <span
                    className="font-extrabold w-6 text-center"
                    style={{
                      fontFamily: 'var(--font-display)',
                      color: RANK_COLORS[item.rank],
                    }}
                  >
                    #{item.rank}
                  </span>
                  <span className="flex-1 font-medium">{item.name}</span>
                  <span
                    className="cv-text-gradient font-bold"
                    style={{ fontFamily: 'var(--font-display)', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {item.views}
                  </span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* ---- Social integrations card (5/12) ---- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="cv-bento-span-5 p-8"
            style={{
              gridColumn: 'span 5',
              background: 'var(--cv-surface)',
              border: '1px solid var(--cv-border)',
              borderRadius: 'var(--radius-lg)',
              transition: 'border-color 180ms, box-shadow 180ms',
            }}
            whileHover={{
              borderColor: 'var(--cv-border-accent)',
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.06)',
            }}
          >
            <div
              className="mb-3"
              style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--cv-accent)',
              }}
            >
              INTEGRATIONS
            </div>
            <div
              className="font-bold mb-2"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)' }}
            >
              TikTok + Instagram
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--cv-text-muted)', lineHeight: 1.6 }}>
              Auto-synced view counts, engagement rates, and content performance from both platforms.
            </div>

            {/* Platform icons */}
            <div className="flex gap-3 mt-6">
              {/* TikTok */}
              <div
                className="flex items-center justify-center"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'var(--cv-surface-2)',
                  border: '1px solid var(--cv-border)',
                }}
                aria-label="TikTok"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                </svg>
              </div>
              {/* Instagram */}
              <div
                className="flex items-center justify-center"
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  background: 'var(--cv-surface-2)',
                  border: '1px solid var(--cv-border)',
                }}
                aria-label="Instagram"
              >
                <svg viewBox="0 0 24 24" width="22" height="22" fill="currentColor">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                </svg>
              </div>
            </div>
          </motion.div>

          {/* ---- Payouts / budget card (7/12) ---- */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-40px' }}
            transition={{ duration: 0.7, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
            className="cv-bento-span-7 p-8"
            style={{
              gridColumn: 'span 7',
              background: 'var(--cv-surface)',
              border: '1px solid var(--cv-border)',
              borderRadius: 'var(--radius-lg)',
              transition: 'border-color 180ms, box-shadow 180ms',
            }}
            whileHover={{
              borderColor: 'var(--cv-border-accent)',
              boxShadow: '0 0 40px rgba(168, 85, 247, 0.06)',
            }}
          >
            <div
              className="mb-3"
              style={{
                fontSize: '11px',
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                color: 'var(--cv-accent)',
              }}
            >
              {view === 'creator' ? 'PAYOUTS' : 'BUDGET'}
            </div>
            <div
              className="font-bold mb-2"
              style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-lg)' }}
            >
              {view === 'creator' ? 'Transparent payouts' : 'Full budget control'}
            </div>
            <div style={{ fontSize: 'var(--text-sm)', color: 'var(--cv-text-muted)', lineHeight: 1.6 }}>
              {view === 'creator'
                ? 'See exactly how much you earned from each campaign. No hidden fees, no surprises.'
                : 'Set daily limits, total caps, and CPM rates. Pause or scale campaigns instantly.'}
            </div>

            {/* Mini payout display */}
            <div
              className="mt-6 flex items-center justify-between"
              style={{
                padding: '20px',
                background: 'var(--cv-surface-2)',
                borderRadius: '12px',
                border: '1px solid var(--cv-border)',
              }}
            >
              <div>
                <div
                  className="uppercase font-medium mb-1"
                  style={{
                    fontSize: '12px',
                    color: 'var(--cv-text-faint)',
                    letterSpacing: '0.08em',
                  }}
                >
                  {payoutLabel}
                </div>
                <div
                  className="cv-text-gradient-green font-extrabold"
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: '28px',
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {payoutValue}
                </div>
              </div>

              <div
                className="text-sm font-semibold"
                style={{
                  padding: '8px 16px',
                  borderRadius: '20px',
                  background: 'rgba(34, 197, 94, 0.1)',
                  color: 'var(--cv-green)',
                }}
              >
                {payoutStatus}
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default PlatformBento;
